// SettingsNav — sidebar nav for the Settings module.
//
// Sections:
//   COMPANY   — Company Settings / Holidays / Notification Settings /
//               Notification Templates / Print Templates / Theme /
//               Time Slots / Products / Work Sources / Payment Modes
//   SYSTEM    — Storage (admin only)
//   PERSONAL  — My Profile / Push Notifications
//
// Same nav-injection pattern as WeighbridgeNav / SkipNav. Reuses the
// shared CollectionsNav.module.css stylesheet.

import { type ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Bell,
  BellRing,
  Briefcase,
  Building2,
  CalendarOff,
  Clock,
  CreditCard,
  Link,
  Mail,
  Package,
  Palette,
  Printer,
  User,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAuthStore } from '../../store/authStore';
import { useSidebar } from '../../components/shell/SidebarContext';
import type { UserSummary } from '../../types/api';
import styles from '../collections/components/CollectionsNav.module.css';

type Role = UserSummary['role'];

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  roles?: Role[];
  end?: boolean;
}

interface NavSection {
  label?: string;
  roles?: Role[];
  items: NavItem[];
}

const STAFF_ROLES: Role[] = ['staff', 'admin'];
const ADMIN_ROLES: Role[] = ['admin'];

const SECTIONS: NavSection[] = [
  {
    label: 'Company',
    roles: STAFF_ROLES,
    items: [
      { to: '/settings/company', label: 'Company Settings', icon: Building2, roles: STAFF_ROLES },
      { to: '/settings/holidays', label: 'Holidays', icon: CalendarOff, roles: STAFF_ROLES },
      { to: '/settings/notifications', label: 'Notification Settings', icon: Bell, roles: STAFF_ROLES },
      { to: '/settings/templates', label: 'Notification Templates', icon: Mail, roles: STAFF_ROLES },
      { to: '/settings/print-templates', label: 'Print Templates', icon: Printer, roles: STAFF_ROLES },
      { to: '/settings/theme', label: 'Theme', icon: Palette, roles: STAFF_ROLES },
      { to: '/settings/time-slots', label: 'Time Slots', icon: Clock, roles: STAFF_ROLES },
      { to: '/settings/products', label: 'Products', icon: Package, roles: STAFF_ROLES },
      { to: '/settings/work-sources', label: 'Work Sources', icon: Briefcase, roles: STAFF_ROLES },
      { to: '/settings/payment-modes', label: 'Payment Modes', icon: CreditCard, roles: STAFF_ROLES },
      { to: '/settings/quick-links', label: 'Quick Links', icon: Link, roles: ADMIN_ROLES },
      { to: '/settings/dwt', label: 'Digital Waste Tracking', icon: Link, roles: ADMIN_ROLES },
    ],
  },
  {
    label: 'Personal',
    roles: STAFF_ROLES,
    items: [
      { to: '/profile', label: 'My Profile', icon: User, roles: STAFF_ROLES },
      { to: '/settings/push', label: 'Push Notifications', icon: BellRing, roles: STAFF_ROLES },
    ],
  },
];

export function SettingsNav(): JSX.Element | null {
  const role = useAuthStore((s) => s.user?.role);
  const { isCollapsed } = useSidebar();

  function visible(roles?: Role[]): boolean {
    if (!roles) return true;
    if (!role) return false;
    return roles.includes(role);
  }

  return (
    <div className={cn(styles.nav, isCollapsed && styles.collapsed)}>
      {SECTIONS.map((section, idx) => {
        if (!visible(section.roles)) return null;
        const items = section.items.filter((it) => visible(it.roles));
        if (items.length === 0) return null;
        return (
          <div key={idx} className={styles.section}>
            {section.label ? (
              <span className={styles.sectionLabel}>{section.label}</span>
            ) : null}
            <ul className={styles.list}>
              {items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => cn(styles.link, isActive && styles.linkActive)}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <span className={styles.linkInner}>
                      <span className={styles.icon}>
                        <item.icon size={18} aria-hidden />
                      </span>
                      <span className={styles.label}>{item.label}</span>
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
